import { isEmpty } from 'lodash';
import styled from 'styled-components';
import {
  cssColorValue,
  LoadingIndicator,
  semanticColors,
  spacer32,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { LiveInTerritoryAvailabilityResponse } from '@hooks/useLiveInTerritoryAvailability/types';

const TerritoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, ${spacer32});

  [data-live='true'] {
    color: ${cssColorValue(semanticColors.textPositive)};
  }

  [data-live='false'] {
    color: ${cssColorValue(semanticColors.textNegative)};
  }
`;

interface LiveInTerritoriesProps {
  isLoading: boolean;
  error: string | null;
  data: LiveInTerritoryAvailabilityResponse | null | undefined;
  handleRetry: () => void;
}

export const LiveInTerritories = ({
  isLoading,
  error,
  data,
  handleRetry,
}: LiveInTerritoriesProps) => {
  const t = useT();
  let liveInTerritoriesArray: [string, boolean][] = [];
  let liveInTerritoriesCount;
  let totalTerritoriesCount;

  if (data && !isEmpty(data)) {
    // Remove if we want to render RU data
    const { RU, ...availabilityWithoutRU } = data?.availability;
    // @ts-ignore
    liveInTerritoriesArray = Object.entries(availabilityWithoutRU).sort();

    liveInTerritoriesCount = liveInTerritoriesArray.filter(
      ([, isLive]) => isLive,
    ).length;

    totalTerritoriesCount = liveInTerritoriesArray.length;
  }

  return (
    <>
      {!isLoading && data && (
        <>
          <Type variant="viola" semanticColor="textBase" as="div">
            {liveInTerritoriesCount} / {totalTerritoriesCount}
          </Type>
          <TerritoryGrid>
            {liveInTerritoriesArray.map(([territory, isAvailable]) => {
              return (
                <Type variant="viola" data-live={isAvailable} key={territory}>
                  {territory}
                </Type>
              );
            })}
          </TerritoryGrid>
        </>
      )}
      {!isLoading && error && (
        <>
          <Type variant="viola" semanticColor="textBase">
            {t(
              'atlas.errors.error_loading',
              'Error loading',
              'Loading error state',
            )}
            <br />
          </Type>
          <TextLink
            semanticColor="textBrightAccent"
            tabIndex={0}
            standalone
            onClick={handleRetry}
          >
            {t(
              'atlas.actions.try_again',
              'Try again',
              'Text button to try fetch data again',
            )}
          </TextLink>
        </>
      )}
      {isLoading && (
        <LoadingIndicator indicatorSize="sm" data-testid="loading" />
      )}
    </>
  );
};
